import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AmpPropsContextProvider from '../../context/amp-props/amp-props-provider';
import { onLiveblogVisibility, isSportsDataLiveMatchPage } from '../../utils/lmp-utils';
import { makeClassNames, IClassNames } from '../../utils/styles';
import './liveblog.scss';
import Timeline from '../timeline/timeline';
import NewPostsTitle from '../new-posts-title/new-posts-title';
import { IPinnedPost } from '../pinned-post/pinned-post';
import { IPost } from '../post/post';
import BadgeNewPosts from '../badge-new-posts/badge-new-posts';
import { LiveblogTenant } from '../../typings/liveblog-themes';
import { CommonTypings } from '../../typings';
import SkyPlayerProvider from '../../context/sky-player/sky-player-provider';
import { IAmpPropsTag } from '../../typings/amp';
import { getAmpTag, isAmp } from '../../utils/amp';
import StripLabel from '../strip-label/strip-label';
import IconPin from '../icons/icon-pin';
import { generateIntCmpFromTenant, setDataLayerElement } from '../../utils/tracking-data';
import { mapPostsPerPage, getMostRecentPostId } from '../../utils/post-utils';
import { setLandToElement } from '../../utils/land-to-element';
import { IPostsPerPage } from '../../typings/common-types';
import { isClient, isMobile } from '../../utils/device-utils';

export interface INewPosts {
  badgeCounter: number;
  labelCounter: number;
  isBadgeShown?: boolean;
  handleBadgeClick?: () => void;
}

export type AcceptedCookiesConfig = {
  [key in CommonTypings.SocialType]?: boolean; // i.e. { instagram: false, youtube: false }
};

export interface ILiveblog {
  labellive?: string;
  liveblogUrlWithQP?: string;
  liveblogUrlAmp?: string;
  tenant: LiveblogTenant;
  pinnedPosts?: Array<IPinnedPost>;
  posts?: Array<IPost>;
  dateNow?: Date;
  newPostsConfig?: INewPosts;
  acceptedCookiesConfig?: AcceptedCookiesConfig;
  isWebView?: boolean;
  ampProps?: Array<IAmpPropsTag>;
  postsPerPageConfig?: IPostsPerPage;
}
const DEFAULT_POSTS_PER_PAGE = { mobile: 10, desktop: 10 };

const classNames = (): IClassNames =>
  makeClassNames({
    Liveblog: 'liveblog',
  });

const Liveblog: React.FC<ILiveblog> = ({
  labellive,
  tenant,
  pinnedPosts,
  liveblogUrlAmp,
  liveblogUrlWithQP,
  posts,
  dateNow = new Date(),
  newPostsConfig,
  acceptedCookiesConfig = {},
  isWebView,
  ampProps,
  postsPerPageConfig = DEFAULT_POSTS_PER_PAGE,
}) => {
  const cn = classNames();
  const { labelCounter = 0, badgeCounter, isBadgeShown } = newPostsConfig || {};
  const [showBadgeInLMPPage, setShowBadgeInLMPPage] = useState<boolean>(false);
  const postsPerPage = isMobile() || !isClient() ? postsPerPageConfig.mobile : postsPerPageConfig.desktop;
  const [postsVisible, setPostsVisible] = useState<number>(postsPerPage);

  const ampVideo: IAmpPropsTag | undefined = getAmpTag(ampProps, 'video');

  const intcmpOnStripLabel = useMemo(() => generateIntCmpFromTenant(tenant, 'liveblog_refresh-cta_null'), [tenant]);
  const intcmpOnBadgeNewPosts = useMemo(
    () => generateIntCmpFromTenant(tenant, 'liveblog_refresh-popup_null'),
    [tenant]
  );

  const mostRecentPostId = getMostRecentPostId({ posts, pinnedPosts });

  useEffect(() => {
    onLiveblogVisibility((isVisible) => {
      setShowBadgeInLMPPage(isVisible);
    });
  }, []);

  const showBadge =
    !!badgeCounter && (!isSportsDataLiveMatchPage() || (isSportsDataLiveMatchPage() && showBadgeInLMPPage));

  const hasPinnedPosts = !!pinnedPosts?.length;

  const ampPropsValues = useMemo(
    () => ({
      ampProps,
      isAmp: isAmp(ampProps),
    }),
    [ampProps]
  );

  // HANDLERS
  const handleBadgeClick = useCallback(() => {
    setLandToElement(mostRecentPostId as string);
    setDataLayerElement(intcmpOnBadgeNewPosts);
  }, [mostRecentPostId, intcmpOnBadgeNewPosts]);

  const handleStripLabelClick = useCallback(() => {
    setLandToElement(mostRecentPostId as string);
    setDataLayerElement(intcmpOnStripLabel);
  }, [mostRecentPostId, intcmpOnStripLabel]);

  const handleInfiniteScroll = () => {
    setPostsVisible(postsVisible + postsPerPage);
  };

  return (
    <AmpPropsContextProvider value={ampPropsValues}>
      <SkyPlayerProvider isAmp={!!ampVideo}>
        <div className={cn.Liveblog}>
          {hasPinnedPosts && (
            <StripLabel label="in evidenza" cssModifier="pinned">
              <IconPin color="white" fill="white" />
            </StripLabel>
          )}
          {hasPinnedPosts && (
            <Timeline
              posts={pinnedPosts}
              isPinned
              dateNow={dateNow}
              newPostsCounter={labelCounter + 1}
              acceptedCookiesConfig={acceptedCookiesConfig}
              tenant={tenant}
              isWebView={isWebView}
              ampProps={ampProps}
            />
          )}
          {labellive && (
            <StripLabel
              href={isAmp(ampProps) ? liveblogUrlAmp : liveblogUrlWithQP}
              label={labellive}
              cssModifier="live"
              onClick={handleStripLabelClick}
            />
          )}
          {!!labelCounter && <NewPostsTitle count={labelCounter} />}
          {!!posts?.length && (
            <Timeline
              posts={mapPostsPerPage({ posts, postsPerPage, postsVisible })}
              dateNow={dateNow}
              newPostsCounter={labelCounter + 1}
              acceptedCookiesConfig={acceptedCookiesConfig}
              tenant={tenant}
              isWebView={isWebView}
              ampProps={ampProps}
              handleInfiniteScroll={handleInfiniteScroll}
            />
          )}
          {showBadge && (
            <BadgeNewPosts
              count={badgeCounter as number}
              isShown={isBadgeShown}
              href={isAmp(ampProps) ? liveblogUrlAmp : liveblogUrlWithQP}
              onClick={handleBadgeClick}
            />
          )}
        </div>
      </SkyPlayerProvider>
    </AmpPropsContextProvider>
  );
};

export default Liveblog;
